import type { SceneryArrays, ZoneBuilder } from '../types';
import { scatterPeople } from '../crowd';
import {
  BOAT_COLORS,
  DANFO_YELLOW,
  LAGOS_BLOCKS,
  LAGOS_GLASS,
  LAGOS_LAGOON,
  UMBRELLA_COLORS,
  darken,
} from '../palettes';

/**
 * scenery/zones/lagos.ts — warm tropical afternoon energy. Balogun's loud
 * commercial mainland (danfos, market umbrellas) → the CAUSEWAY: open lagoon
 * on BOTH sides with the Makoko stilt village out on the water → Victoria
 * Island's glass towers → the run to the Lekki-Ikoyi bridge.
 *
 * lagos-ts (graphics overhaul) — the dressing rules used below:
 *  - EVERY new prop is derived from an INDEX/POSITION HASH, never a new rng
 *    draw, so the whole city's layout stays byte-identical (the M36 rule).
 *    `h01` is the only randomness source added by this round.
 *  - Dressing rides `a.plain` (one shared InstancedMesh) so the added
 *    fidelity costs ZERO extra draw calls.
 *  - Local frame convention matches the builders' `yawQ(rotY)`: local +x maps
 *    to world (cos, −sin), local +z to (sin, cos). `loc()` below is the one
 *    place that maths lives.
 */

/** Deterministic 0..1 from an integer — variety WITHOUT touching the rng
 *  stream (adding a prop must never shift a building by a millimetre). */
function h01(n: number): number {
  let x = (n | 0) ^ 0x9e3779b9;
  x = Math.imul(x ^ (x >>> 15), 0x85ebca6b);
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
  return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
}

/** World position of a prop-local (lx, lz) offset around a yawed anchor.
 *  MUST match builders' `yawQ` or dressing drifts off its host mesh. */
function loc(x: number, z: number, rotY: number, lx: number, lz: number): [number, number] {
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  return [x + lx * c + lz * s, z - lx * s + lz * c];
}

// ---- Balogun shop boards --------------------------------------------------
// The hand-painted boards were blank solid planks (QA: "the market's loud
// signage energy is missing"). They are PAINTED here with geometry rather
// than a canvas: a contrasting field band, a top pinstripe and a row of dark
// glyph blocks that read as a trade name at racing distance. All of it is
// `plain`, so it stays one draw call. (A real shared canvas atlas would be
// finer still — that needs a textured board array in builders.ts; flagged.)
const BOARD_INK = 0x231a12;
const BOARD_TRIM = [0xf2f0ea, 0xe0a818, 0xc84a30, 0x2a9a6a, 0x2a6ac8, 0x16181d];

function paintBoard(
  a: SceneryArrays,
  x: number,
  y: number,
  z: number,
  dir: number,
  len: number,
  bg: number,
  seed: number,
): void {
  // A hair thicker than the 0.16 plank so every layer stands proud (no
  // z-fighting) while still reading as paint ON the board.
  const T = 0.22;
  let trim = BOARD_TRIM[(h01(seed) * BOARD_TRIM.length) | 0];
  if (trim === bg) trim = BOARD_TRIM[(((h01(seed) * BOARD_TRIM.length) | 0) + 1) % BOARD_TRIM.length];
  const fwdX = Math.sin(dir);
  const fwdZ = Math.cos(dir);
  const push = (off: number, dy: number, h: number, d: number, color: number): void => {
    a.plain.push({
      x: x + fwdX * off,
      y: y + dy,
      z: z + fwdZ * off,
      rotY: dir,
      w: T,
      h,
      d,
      color,
    });
  };
  push(0, 0.06, 0.24, len * 0.94, trim); // the painted field band
  push(0, 0.88, 0.07, len * 0.94, trim); // top pinstripe
  // The "trade name": 3-4 dark word blocks marching along the plank.
  const words = 3 + ((h01(seed + 11) * 2) | 0);
  const span = len * 0.78;
  for (let i = 0; i < words; i++) {
    const t = words === 1 ? 0.5 : i / (words - 1);
    const wLen = span / words * (0.45 + h01(seed + i * 7 + 3) * 0.4);
    push(-span / 2 + t * span, 0.4, 0.3, wLen, BOARD_INK);
  }
}

// ---- Makoko stilt hut dressing --------------------------------------------
// The zone huts were flat boxes on toothpick legs while the M117 jetty next
// door is the project's material bar. This gives each one a plank-batten
// wall rhythm, a real dark door + window openings, and the veranda deck on
// posts that every Makoko house has. Geometry keyed to builders'
// STILT_BODY (4.6 x 2.6 x 3.6, centre y+4.6) and STILT_LEG (3.4 tall).
const HUT_TIMBER = 0x7a5f3c;
const HUT_POST = 0x4a3a28;
const HUT_HOLE = 0x241b13;

function dressStiltHut(
  a: SceneryArrays,
  x: number,
  y: number,
  z: number,
  rotY: number,
  color: number,
): void {
  const seed = (x * 31 + z * 17) | 0;
  const box = (
    lx: number,
    lz: number,
    dy: number,
    w: number,
    h: number,
    d: number,
    c: number,
  ): void => {
    const [wx, wz] = loc(x, z, rotY, lx, lz);
    a.plain.push({ x: wx, y: y + dy, z: wz, rotY, w, h, d, color: c });
  };
  // Veranda deck on the ROAD-facing side (local +x), on its own two posts.
  box(3.55, 0, 3.28, 2.5, 0.16, 2.6, HUT_TIMBER);
  box(4.72, 0, 3.44, 0.14, 0.44, 2.6, HUT_POST); // outer rail
  for (const lz of [-1.08, 1.08]) box(4.58, lz, 0, 0.22, 3.44, 0.22, HUT_POST);
  // Doorway onto the deck + a window on the down-course wall.
  box(2.36, 0, 3.35, 0.12, 1.75, 0.95, HUT_HOLE);
  box(0.55, 1.86, 4.5, 0.78, 0.55, 0.12, HUT_HOLE);
  if (h01(seed) < 0.6) box(-2.36, -0.4, 4.5, 0.12, 0.5, 0.72, HUT_HOLE); // lagoon-side window
  // Two plank battens across the down-course wall — the jetty's board rhythm
  // at hut scale, so the wall stops reading as one poured slab.
  const batten = darken(color, 0.68);
  for (const dy of [3.82, 5.06]) box(0, 1.85, dy, 4.5, 0.09, 0.1, batten);
}

// ---- Canoe dressing -------------------------------------------------------
// Pirogues read as flat lozenges (the hull is one box). A dark gunwale strip
// down each side plus a pale thwart plank across the middle give the eye the
// open-boat silhouette. Keyed to builders' BOAT_HULL (1.5 x 0.7 x 6.2,
// centre y+0.35 above the instance y, scaled by `s` in x and z only).
function dressCanoe(
  a: SceneryArrays,
  x: number,
  y: number,
  z: number,
  rotY: number,
  s: number,
): void {
  const gun = 0x3a2c1e;
  for (const lx of [-1, 1]) {
    const [wx, wz] = loc(x, z, rotY, lx * (0.75 * s - 0.07), 0);
    a.plain.push({ x: wx, y: y + 0.62, z: wz, rotY, w: 0.17, h: 0.16, d: 5.9 * s, color: gun });
  }
  a.plain.push({ x, y: y + 0.66, z, rotY, w: 1.42 * s, h: 0.1, d: 0.34, color: 0x9a8156 });
}

const balogun: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a }) => {
  // Loud mid-rise commercial blocks, painted every colour that sells.
  if (rng.next() < 0.9) {
    const w = rng.range(8, 14);
    const h = rng.range(7, 16);
    const d = rng.range(8, 13);
    const lat = hw + 2.5 + w / 2 + rng.range(0, 4);
    const [x, y, z] = at(lat);
    const color = rng.pick(LAGOS_BLOCKS);
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
    a.plain.push({ x, y: y + h - 0.3, z, rotY: s.dir, w: w + 0.7, h: 0.7, d: d + 0.7, color: darken(color, 0.7) });
    // The Balogun signatures (M14): a rusty CORRUGATED shopfront awning on
    // posts, a stack of hand-painted shop boards above it, and the rooftop
    // tank stand every Lagos block carries.
    const [ax, ay, az] = at(lat - w / 2 - 1.3);
    a.plain.push({ x: ax, y: ay + 3.1, z: az, rotY: s.dir, w: 2.7, h: 0.14, d: d * 0.8, color: rng.pick([0x9a5a30, 0x8a6a3a, 0x7a6a55]) }); // corrugated awning
    a.plain.push({ x: ax, y: ay + 1.5, z: az, rotY: s.dir, w: 0.14, h: 3.0, d: 0.14, color: 0x4a4038 }); // awning post
    const [sx2, sy2, sz2] = at(lat - w / 2 - 0.12);
    for (let b = 0; b < 2; b++) {
      // The two rng draws below are LOAD-BEARING and must stay in this order
      // (length, then colour) — every prop downstream depends on the stream.
      const bLen = rng.range(2.4, 4);
      const bg = rng.pick([0xe0a818, 0x2a9a6a, 0xc84a30, 0x2a6ac8, 0xf2f0ea]);
      const by = sy2 + 4.2 + b * 1.3;
      a.plain.push({ x: sx2, y: by, z: sz2, rotY: s.dir, w: 0.16, h: 1.0, d: bLen, color: bg });
      paintBoard(a, sx2, by, sz2, s.dir, bLen, bg, ((sx2 * 41 + sz2 * 13) | 0) + b * 97);
    }
    if (rng.next() < 0.5) {
      const [tx2, ty2, tz2] = at(lat + rng.range(-1, 1));
      a.plain.push({ x: tx2, y: ty2 + h + 0.7, z: tz2, rotY: s.dir, w: 1.2, h: 1.4, d: 1.2, color: 0x16181d }); // rooftop tank
    }
  }
  // Danfos working the kerb + market umbrellas between.
  if (rng.next() < 0.14) {
    const [x, y, z] = at(hw + 3 + rng.range(0, 1.5));
    a.buses.push({ x, y, z, rotY: s.dir, color: DANFO_YELLOW });
  }
  if (rng.next() < 0.3) {
    const [x, y, z] = at(hw + 2.2 + rng.range(0, 2));
    a.umbrellas.push({ x, y, z, rotY: 0, s: rng.range(0.9, 1.25), color: rng.pick(UMBRELLA_COLORS) });
  }
  // Balogun market — Lagos street energy, packed.
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.5, hw + 6], chance: 0.85, count: 5 });
};

const lagoon: ZoneBuilder = ({ rng, hw, at, s, a, side, step }) => {
  // The causeway: open water on BOTH sides of the road.
  {
    const w = rng.range(60, 80);
    const [x, y, z] = at(hw + w / 2 + 1);
    a.ponds.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: LAGOS_LAGOON, arc: s.distance, side, shore: hw + 1 });
  }
  if (side === -1) {
    // Makoko: stilt houses standing out in the lagoon.
    if (rng.next() < 0.4) {
      const [x, y, z] = at(hw + 10 + rng.range(0, 26));
      // Draw order is load-bearing: lateral, then yaw, then colour.
      const rotY = s.dir + rng.range(-0.5, 0.5);
      const color = rng.pick([0x9a8a6a, 0xb0a080, 0x8a7a5a, 0x7a8a96]);
      a.stiltHouses.push({ x, y, z, rotY, color });
      dressStiltHut(a, x, y, z, rotY, color);
    }
  }
  // Canoes threading the water on either side. lagos-ts: the base lateral was
  // hw+8, which put a pirogue's 6.2-u hull right against the glowing chevron
  // barrier — from any elevated view the wall ate half the boat and it read
  // as clipping through it (QA). hw+13 keeps the worst case (broadside, full
  // scale) ~10 u clear of the rail and still well inside the 60-80 u pond.
  if (rng.next() < 0.18) {
    const [x, y, z] = at(hw + 13 + rng.range(0, 20));
    const rotY = rng.range(0, Math.PI * 2);
    const color = rng.pick(BOAT_COLORS);
    const bs = rng.range(0.7, 1.0);
    a.boats.push({ x, y: y + 0.12, z, rotY, color, s: bs });
    dressCanoe(a, x, y + 0.12, z, rotY, bs);
  }
};

const victoriaIsland: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, progress, marks }) => {
  // Clear a forecourt at the National Theatre + Cocoa House so they read.
  const theatreAt = marks.nationalTheatre;
  if (theatreAt != null && Math.abs(progress - theatreAt) < 0.025 && side === -1) return;
  const cocoaAt = marks.cocoaHouse;
  if (cocoaAt != null && Math.abs(progress - cocoaAt) < 0.02 && side === 1) return;
  if (rng.next() < 0.85) {
    const w = rng.range(10, 16);
    const h = rng.range(16, 40);
    const [x, y, z] = at(hw + 2.5 + w / 2 + rng.range(0, 5));
    const pop = rng.next() < 0.18;
    a.buildings.push({
      x,
      y,
      z,
      rotY: s.dir,
      w,
      h,
      d: rng.range(9, 15),
      color: pop ? rng.pick([0xe0a818, 0x2a9a6a, 0xc84a30]) : rng.pick(LAGOS_GLASS),
    });
  }
  if (rng.next() < 0.16) {
    const [x, y, z] = at(hw + 2.4);
    a.palms.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.9, 1.2) });
  }
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.6, hw + 5], chance: 0.5, count: 3 });
};

const lekki: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, step }) => {
  // Water returns on the right as the bridge nears; palms + low villas left.
  if (side === 1) {
    const w = rng.range(50, 70);
    const [x, y, z] = at(hw + w / 2 + 1);
    a.ponds.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: LAGOS_LAGOON, arc: s.distance, side, shore: hw + 1 });
  } else {
    if (rng.next() < 0.45) {
      const w = rng.range(9, 13);
      const h = rng.range(5, 9);
      const [x, y, z] = at(hw + 4 + w / 2 + rng.range(0, 5));
      a.buildings.push({ x, y, z, rotY: s.dir, w, h, d: rng.range(9, 13), color: rng.pick(LAGOS_BLOCKS) });
    }
    if (rng.next() < 0.35) {
      const [x, y, z] = at(hw + 2.4);
      a.palms.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.9, 1.3) });
    }
    scatterPeople({ peopleRng, at, a }, { band: [hw + 1.6, hw + 5], chance: 0.35, count: 2 });
  }
};

export const LAGOS_ZONES = { balogun, lagoon, victoriaIsland, lekki };
